const THEME_INFO = /* @__PURE__ */ new WeakMap();
function createTheme(palette, definition, options) {
  const theme = {
    ...Object.fromEntries(
      Object.entries(definition).map(([key, offset]) => {
        return [key, getValue(palette, offset)];
      })
    ),
    ...options == null ? void 0 : options.nonInheritedValues
  };
  THEME_INFO.set(theme, { palette, definition, cache: /* @__PURE__ */ new Map() });
  return theme;
}
const getValue = (palette, value) => {
  if (typeof value === "string")
    return value;
  const max = palette.length - 1;
  const isPositive = value === 0 ? !isMinusZero(value) : value >= 0;
  const next = isPositive ? value : max + value;
  const index = Math.min(Math.max(0, next), max);
  return palette[index];
};
function addChildren(themes, getChildren) {
  const out = { ...themes };
  for (const key in themes) {
    const subThemes = getChildren(key, themes[key]);
    for (const sKey in subThemes) {
      out[`${key}_${sKey}`] = subThemes[sKey];
    }
  }
  return out;
}
const skipMask = (template, { skip }) => {
  if (!skip)
    return template;
  return Object.fromEntries(
    Object.entries(template).filter(([k]) => !(k in skip))
  );
};
const createShiftMask = ({ inverse } = {}) => {
  return (template, opts) => {
    const { override, max: maxIn, palette, min = 0, strength = 1 } = opts;
    const values = Object.entries(template);
    const max = maxIn ?? (palette ? Object.values(palette).length - 1 : Infinity);
    const out = {};
    for (const [key, value] of values) {
      if (typeof value === "string")
        continue;
      if (typeof (override == null ? void 0 : override[key]) === "number") {
        const overrideShift = override[key];
        out[key] = value + overrideShift;
        continue;
      }
      const isPositive = value === 0 ? !isMinusZero(value) : value >= 0;
      const direction = isPositive ? 1 : -1;
      const invert = inverse ? -1 : 1;
      const next = value + strength * direction * invert;
      const clamped = isPositive ? Math.max(min, Math.min(max, next)) : Math.min(-min, Math.max(-max, next));
      out[key] = clamped;
    }
    return skipMask(out, opts);
  };
};
const createWeakenMask = () => createShiftMask();
const createStrengthenMask = () => createShiftMask({ inverse: true });
function isMinusZero(value) {
  return 1 / value === -Infinity;
}
function applyMask(theme, mask, options = {}) {
  const info = THEME_INFO.get(theme);
  if (!info) {
    throw new Error(
      process.env.NODE_ENV !== "production" ? `No info found for theme, you must pass the theme created by createThemeFromPalette directly to extendTheme` : `\u274C Err2`
    );
  }
  const template = mask(info.definition, {
    palette: info.palette,
    ...options
  });
  const next = createTheme(info.palette, template);
  return next;
}
if (process.env.NODE_ENV === "development") {
  const palette = ["0", "1", "2", "3", "-3", "-2", "-1", "-0"];
  const template = { bg: 1, fg: -1 };
  const stongerMask = createStrengthenMask();
  const weakerMask = createWeakenMask();
  const theme = createTheme(palette, template);
  if (theme.bg !== "1")
    throw `\u274C`;
  if (theme.fg !== "-1")
    throw `\u274C`;
  const str = applyMask(theme, stongerMask);
  if (str.bg !== "0")
    throw `\u274C`;
  if (str.fg !== "-0")
    throw `\u274C`;
  const weak = applyMask(theme, weakerMask);
  if (weak.bg !== "2")
    throw `\u274C`;
  if (weak.fg !== "-2")
    throw `\u274C`;
  const weak2 = applyMask(theme, weakerMask, { strength: 2 });
  if (weak2.bg !== "3")
    throw `\u274C`;
  if (weak2.fg !== "-3")
    throw `\u274C`;
}
export {
  addChildren,
  applyMask,
  createShiftMask,
  createStrengthenMask,
  createTheme,
  createWeakenMask,
  skipMask
};
//# sourceMappingURL=index.js.map
